// Round 3 (2026-09-10 판매자 지시 §2·§3·§9·§10·§11·§18·§19)
//   · Mobile Hero 확대 · full-viewport aspect (min-h 로 시원한 몰입감)
//   · Prev/Next null 시 자연스러운 collapse (§10)
//   · Watch Hub 하단 "다른 LIVE" 섹션 (§11 · 기존 HomeLiveCard REUSE · §12)
//   · scrollTop preserved (Round 1 §18)
//   · 사용자 UI 에 개발자 문구 절대 노출 안 함 (§19)
// Round 5c (2026-09-10 §6·§7·§8·§9)
//   · Mobile hero 좌우 swipe = 기존 Prev/Next queue REUSE (새 API 생성 X).
//   · Vertical scroll 우선 · horizontal threshold 60 px · |dx| > |dy| * 1.4.
//   · 연속 swipe 중복 navigation 방어 (useNavigate 실행 후 lock).
//   · Desktop mouse UX 무영향 · 버튼 Prev/Next 그대로 유지.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import HomeLiveCard from '@/components/HomeLiveCard';

const PLATFORM_BADGE = {
  naver_shopping_live: { color: '#03C75A', text: '#ffffff', label: '네이버 쇼핑라이브', cta: '네이버에서 방송보기' },
  kakao_shopping_live: { color: '#FEE500', text: '#1a1a1a', label: '카카오 쇼핑라이브', cta: '카카오에서 방송보기' },
};

const LiveWatchHub = () => {
  const { externalId } = useParams();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (id) => {
    setLoading(true);
    try { const r = await api.get(`/live/watch/${id}`); setData(r.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    if (externalId) load(externalId);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [externalId, load]);

  // Round 5c · Mobile swipe on Hero · reuses `goto()` + `data.prev` / `data.next`
  // queue exactly like the on-screen Prev/Next buttons.
  const navLockRef = useRef(false);   // prevents rapid double-swipe → double-navigate
  const swipeRef   = useRef({ x0: null, y0: null, active: false });
  const goto = (nid) => {
    if (!nid || navLockRef.current) return;
    navLockRef.current = true;
    navigate(`/live/hub/${nid}`);
  };
  // Reset the swipe lock when a new externalId is loaded.
  useEffect(() => { navLockRef.current = false; }, [externalId]);

  const onHeroTouchStart = (e) => {
    if (!e.touches || e.touches.length !== 1) { swipeRef.current.active = false; return; }
    swipeRef.current = { x0: e.touches[0].clientX, y0: e.touches[0].clientY, active: true };
  };
  const onHeroTouchEnd = (e) => {
    const s = swipeRef.current;
    if (!s.active) return;
    swipeRef.current.active = false;
    const t = (e.changedTouches && e.changedTouches[0]) || null;
    if (!t) return;
    const dx = t.clientX - s.x0, dy = t.clientY - s.y0;
    // Ignore vertical-dominant gestures (page scroll) — |dx| > |dy| * 1.4.
    if (Math.abs(dx) < 60) return;
    if (Math.abs(dx) < Math.abs(dy) * 1.4) return;
    if (dx < 0 && data?.next) goto(data.next.external_id);       // swipe left  → next
    else if (dx > 0 && data?.prev) goto(data.prev.external_id);  // swipe right → prev
  };

  if (loading) {
    return (<div className="min-h-screen flex items-center justify-center" data-testid="watch-hub-loading">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF3B30]" />
    </div>);
  }
  if (!data?.current) {
    return (<div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-gray-500" data-testid="watch-hub-empty">
      <div>방송 정보를 찾을 수 없습니다.</div>
      <Link to="/live/all" className="px-4 py-2 rounded-full bg-black/5 text-xs font-bold hover:bg-black/10">지금 LIVE 목록</Link>
    </div>);
  }
  const cur = data.current;
  const badge = PLATFORM_BADGE[cur.platform] || { color: '#FF3B30', text: '#ffffff', label: cur.platform_name || 'LIVE', cta: '원본에서 보기' };
  const canPlayInApp = data.capability === 'IN_APP_PLAYBACK_ALLOWED';
  const hasPrev = !!data.prev, hasNext = !!data.next;
  const others = data.other_lives || [];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-16" data-testid="watch-hub-page">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 pt-3 sm:pt-6">
        <Link to="/live/all" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black dark:hover:text-white mb-2" data-testid="watch-hub-back">
          <ChevronLeft size={14} /> 지금 LIVE 목록
        </Link>

        {/* §3 · Mobile 확대 Hero — min-h 로 시원한 몰입감 확보 (aspect-video 보다 큼) */}
        {/* Round 5c · Mobile Hero swipe target · Desktop 은 touch 이벤트가 없어 무영향 */}
        {canPlayInApp ? (
          <div className="rounded-2xl overflow-hidden bg-black aspect-video relative sm:aspect-video min-h-[280px] sm:min-h-0"
               data-testid="watch-hub-player"
               onTouchStart={onHeroTouchStart}
               onTouchEnd={onHeroTouchEnd}>
            <iframe src={cur.embed_url || cur.original_url} title={cur.title}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                    allowFullScreen
                    data-testid="watch-hub-embed-iframe" />
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 relative aspect-[4/5] sm:aspect-video min-h-[320px] sm:min-h-0"
               data-testid="watch-hub-hero"
               onTouchStart={onHeroTouchStart}
               onTouchEnd={onHeroTouchEnd}>
            <SafeImg src={cur.thumbnail_url} kind="broadcast" alt={cur.title}
                     className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-3 left-3 flex gap-2">
              {cur.status === 'LIVE' && (
                <span className="px-2.5 py-1 rounded-full bg-[#FF3B30] text-white text-[11px] font-bold animate-pulse" data-testid="watch-hub-live-badge">🔴 LIVE</span>
              )}
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                    style={{ background: badge.color, color: badge.text }}
                    data-testid={`watch-hub-platform-${cur.platform}`}>{badge.label}</span>
            </div>
            {/* Round 5c · Mobile-only swipe hint · Desktop 은 sm:hidden */}
            {(hasPrev || hasNext) && (
              <div className="sm:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-[10px] font-bold"
                   data-testid="watch-hub-swipe-hint">
                {hasPrev && <span>← 이전</span>}
                {hasPrev && hasNext && <span className="opacity-50">·</span>}
                {hasNext && <span>다음 →</span>}
              </div>
            )}
          </div>
        )}

        {/* Meta */}
        <div className="mt-4 sm:mt-5">
          <h1 className="text-lg sm:text-2xl font-black leading-tight" data-testid="watch-hub-title">{cur.title}</h1>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            {cur.seller_name && <span data-testid="watch-hub-seller">👤 {cur.seller_name}</span>}
            {cur.category    && <span data-testid="watch-hub-category">🏷 {cur.category}</span>}
            {cur.scheduled_at && (
              <span data-testid="watch-hub-time">
                🕐 {new Date(cur.scheduled_at).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>

          {/* §8 · Native 는 CTA 숨김 · External 만 노출 */}
          {!canPlayInApp && (
            <a href={cur.original_url} target="_blank" rel="noopener noreferrer"
               className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm shadow-sm hover:shadow"
               style={{ background: badge.color, color: badge.text }}
               data-testid="watch-hub-external-cta">
              <ExternalLink size={16} /> {badge.cta}
            </a>
          )}
        </div>

        {/* §9·§10 · Prev/Next — null 시 자연스러운 collapse */}
        {(hasPrev || hasNext) && (
          <div className="mt-6 grid gap-2 sm:gap-3" style={{ gridTemplateColumns: (hasPrev && hasNext) ? '1fr 1fr' : '1fr' }} data-testid="watch-hub-nav">
            {hasPrev && (
              <button type="button" onClick={() => goto(data.prev.external_id)}
                      className="group flex items-center gap-2 p-3 rounded-xl border border-black/10 dark:border-white/15 bg-white dark:bg-[#141414] hover:border-black/50 hover:shadow-sm text-left"
                      data-testid="watch-hub-prev">
                <ChevronLeft size={18} className="shrink-0 text-gray-500 group-hover:text-black dark:group-hover:text-white" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold text-gray-500 mb-0.5">← 이전 LIVE</div>
                  <div className="text-xs font-bold line-clamp-2">{data.prev.title}</div>
                </div>
              </button>
            )}
            {hasNext && (
              <button type="button" onClick={() => goto(data.next.external_id)}
                      className="group flex items-center gap-2 p-3 rounded-xl border border-black/10 dark:border-white/15 bg-white dark:bg-[#141414] hover:border-black/50 hover:shadow-sm text-right"
                      data-testid="watch-hub-next">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold text-gray-500 mb-0.5">다음 LIVE →</div>
                  <div className="text-xs font-bold line-clamp-2">{data.next.title}</div>
                </div>
                <ChevronRight size={18} className="shrink-0 text-gray-500 group-hover:text-black dark:group-hover:text-white" />
              </button>
            )}
          </div>
        )}

        {/* §11·§12 · 다른 LIVE — HomeLiveCard REUSE · 2/4 cols responsive */}
        {others.length > 0 && (
          <div className="mt-8" data-testid="watch-hub-others">
            <h2 className="text-sm sm:text-base font-black mb-3 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              🔴 지금 다른 방송
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {others.slice(0, 4).map((it) => (<HomeLiveCard key={it.external_id} live={it} />))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveWatchHub;
